import { useMemo, useContext, useCallback } from "react"
import DataIntegrator from "gatherers/data-integrator"
import MultiLineGraph from "renderers/multi-line-graph"
import { createPerformanceCumulativeMultiDateQuery } from "gatherers/multi-date/cumulative-performance"
import { transformPerformanceData, useCurrentPerformanceItems } from "connected-components/graph/hooks/use-performance-items"
import { filterItemsWithData } from "connected-components/graph/helpers"
import { useBaseWithSegment } from "connected-components/dashboard/helpers"
import { computeFilteredSegment } from "helpers/segments"
import { useThemeChartColors } from "hooks/use-theme-chart-colors"
import { MODEL_METADATA_SHAPE } from "connected-components/asset-view/prop-types"
import { GRAPH_CONTEXT_SHAPE } from "../../prop-types"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { getIntradayPriceRefreshTime } from "connected-components/dashboard/helpers/date-calculations"
import { useIsRfrAttributionEnabled } from "resources/account"
import { PERFORMANCE_CONTEXT } from "./context"
export default function PerformanceGraph({ modelMetadata, context }) {
  const { startDate, performanceStartDate, performanceEndDate, base, filter, normalization, instrument, disabledItems, setDisabledItems, setOnlyItem, enableAllItems, intradayBatchMetadata, instrumentsMetadata } = useContext(context)
  const { selectedCategory } = useContext(PERFORMANCE_CONTEXT)
  const [{ isIntradayMode }] = useIntradayState()
  const isRfrAttributionEnabled = useIsRfrAttributionEnabled()

  const baseWithSegment = useBaseWithSegment(base, filter, normalization)
  const segment = useMemo(() => computeFilteredSegment(filter, normalization), [filter, normalization])
  const instrumentWithSegment = useMemo(() => ({ ...instrument, segment }), [instrument, segment])

  const categories = useMemo(() => {
    return modelMetadata.categories.map(({ id, label, color }) => ({
      id,
      label,
      color,
    }))
  }, [modelMetadata])

  const factorsMetadata = useMemo(() => {
    return modelMetadata.factors
  }, [modelMetadata])

  const currentItems = useCurrentPerformanceItems({
    categories,
    factorsMetadata,
    disabledItems,
    selectedCategory,
    isRfrAttributionEnabled,
  })

  const chartColors = useThemeChartColors()
  const filterItems = useCallback(
    (items, data) => filterItemsWithData(items, data, chartColors.series),
    [chartColors.series],
  )

  const intradayPriceRefreshTime = getIntradayPriceRefreshTime(intradayBatchMetadata, performanceStartDate, performanceEndDate)

  const gatherers = useMemo(() => {
    return [createPerformanceCumulativeMultiDateQuery(
      modelMetadata.id,
      instrumentWithSegment,
      baseWithSegment,
      performanceStartDate,
      performanceEndDate,
      isIntradayMode,
      { isRfrAttributionEnabled, intradayPriceRefreshTime, batchId: intradayBatchMetadata?.id, intradayEnabled: instrumentsMetadata?.original?.intradayEnabled, intradayBatchMetadata },
    )]
  }, [modelMetadata, instrumentWithSegment, performanceStartDate, performanceEndDate, baseWithSegment, isIntradayMode, isRfrAttributionEnabled, intradayPriceRefreshTime, intradayBatchMetadata, instrumentsMetadata])

  return <DataIntegrator
    dataTransform={data => transformPerformanceData(data, categories)}
    metrics={gatherers}
    Renderer={MultiLineGraph}
    rendererProps={{
      items: currentItems,
      filterItems,
      endDate: performanceEndDate,
      startDate,
      setItems: setDisabledItems,
      setOnlyItem,
      enableAllItems,
      loadingMessage: "Loading performance data...",
      errorMessage: "Failed to load performance data. Please try again.",
      emptyMessage: "No performance data available.",
    }}
  />
}

PerformanceGraph.propTypes = {
  modelMetadata: MODEL_METADATA_SHAPE.isRequired,
  context: GRAPH_CONTEXT_SHAPE.isRequired,
}
